import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { RouteFallback } from '@/shared/components/RouteFallback';
import { signInRedirect } from './signInRedirect';
import { useSession } from './useSession';

/**
 * Gate for routes that need a signed-in user.
 *
 * The `loading` state renders the same fallback as a lazy chunk rather than
 * redirecting: bouncing someone to `/signin` while their stored session is
 * still being read would log out every returning visitor on every reload.
 *
 * This is a convenience, not a security boundary. The database is the boundary
 * — every table is behind RLS, so a client that skipped this component still
 * reads nothing (CLAUDE.md, invariant 1).
 */
export function RequireSession({ children }: { children: ReactNode }) {
  const { status } = useSession();
  const location = useLocation();

  if (status === 'loading') return <RouteFallback />;

  if (status === 'signedOut') {
    return <Navigate to={signInRedirect(location)} replace />;
  }

  return <>{children}</>;
}
